import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, CreditCard, ExternalLink, Loader2, Star, ShieldCheck, Sparkles, MessageCircle, Mail, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ── Types ──────────────────────────────────────────────────────────────────

type BillingPeriod = 'monthly' | 'annual';

interface PlanConfig {
  id: string;
  annualId?: string;
  name: string;
  monthlyPrice: string;
  annualPrice: string;
  annualSavings: string;
  annualBilled: string;
  tagline: string;
  highlight: boolean;
  features: string[];
}

// ── Plan definitions ────────────────────────────────────────────────────────

const PLANS: PlanConfig[] = [
  {
    id: 'personal',
    annualId: 'personal_annual',
    name: 'Personal',
    monthlyPrice: '$9',
    annualPrice: '$79',
    annualSavings: 'Save $29/yr',
    annualBilled: 'Billed as $79/yr',
    tagline: 'Full control of your feed across all major platforms.',
    highlight: true,
    features: [
      'Reddit + X + Bluesky + YouTube',
      'Up to 20 Listening Rules',
      'Up to 20 Fences',
      'RSS / Newsletter feeds',
      'AI content scoring',
      'Public curator feed',
      'Email support',
    ],
  },
  {
    id: 'creator',
    annualId: 'creator_annual',
    name: 'Creator',
    monthlyPrice: '$29',
    annualPrice: '$249',
    annualSavings: 'Save $99/yr',
    annualBilled: 'Billed as $249/yr',
    tagline: 'For influencers, journalists, and researchers who live in their feed.',
    highlight: false,
    features: [
      'Everything in Personal',
      'Unlimited rules + fences',
      'Up to 200 X accounts monitored',
      '5,000 posts/day across platforms',
      '30 AI briefings/month',
      '50 feed chat queries/day',
      'Priority support',
    ],
  },
  {
    id: 'family',
    annualId: 'family_annual',
    name: 'Family',
    monthlyPrice: '$15',
    annualPrice: '$129',
    annualSavings: 'Save $51/yr',
    annualBilled: 'Billed as $129/yr',
    tagline: 'Everything in Personal, plus child profiles and Parent Lock.',
    highlight: false,
    features: [
      'Everything in Personal',
      'All platforms (incl. future)',
      'Unlimited rules + fences',
      'Parent Lock + child profiles',
      'Priority email support',
    ],
  },
];

const FOUNDING = {
  id: 'founding',
  name: 'Founding Member',
  price: '$49/yr',
  tagline: 'Personal-tier access · Founding badge · 500 spots only',
};

// ── Component ───────────────────────────────────────────────────────────────

export default function Upgrade() {
  const { user } = useAuth();
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  const [loading, setLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [isFounding, setIsFounding] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('subscription_tier, is_founding_member')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setCurrentTier(data?.subscription_tier ?? 'free');
        setIsFounding(Boolean(data?.is_founding_member));
        setSubscriptionLoading(false);
      });
  }, [user?.id]);

  const isSubscribed = currentTier && currentTier !== 'free';

  const startCheckout = async (tier: string) => {
    setLoading(tier);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Please sign in first'); return; }
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier },
      });
      if (error || !data?.url) {
        const msg = error?.message || data?.error || 'Could not start checkout — please try again';
        toast.error(msg);
        console.error('[Checkout error]', { tier, error, data });
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      console.error('[Checkout exception]', e);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error || !data?.url) {
        toast.error('Could not open billing portal — please try again');
        return;
      }
      window.location.href = data.url;
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const getTierId = (plan: PlanConfig) =>
    billing === 'annual' && plan.annualId ? plan.annualId : plan.id;

  const isCurrentPlan = (plan: PlanConfig) => {
    if (!currentTier) return false;
    return currentTier === plan.id || currentTier === plan.annualId;
  };

  return (
    <div className="max-w-3xl mx-auto py-8 space-y-8">

      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Billing &amp; Plans</h1>
        <p className="text-muted-foreground text-sm">
          Manage your subscription and payment details.
        </p>
      </div>

      {/* Why we charge — data commitment callout */}
      <div className="rounded-xl border border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.03] px-5 py-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-[#1A2E4A] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[#1A2E4A]">You pay for the product. We never sell your data.</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Free social media is free because <em>you're</em> the product — your attention and behaviour are sold
            to advertisers. SocialScoop charges a subscription so we have zero incentive to harvest, sell,
            or monetise what you read, filter, or care about. Your feed activity is yours, full stop.
          </p>
        </div>
      </div>

      {/* Current plan banner */}
      {!subscriptionLoading && (
        <Card className={`p-4 flex items-center justify-between gap-4 ${isSubscribed ? 'border-[#1A2E4A]/30 bg-[#1A2E4A]/[0.03]' : 'border-border'}`}>
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="font-medium text-sm">
                {isFounding
                  ? 'Founding Member — active'
                  : isSubscribed
                    ? `${currentTier!.replace('_annual', '').replace(/^\w/, c => c.toUpperCase())} plan — active`
                    : 'Free plan'}
              </p>
              <p className="text-xs text-muted-foreground">
                {isSubscribed
                  ? 'Cancel or update your payment method via the billing portal below.'
                  : 'Upgrade to unlock Reddit, X, and unlimited rules.'}
              </p>
            </div>
          </div>
          {isSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={openBillingPortal}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-1.5" />
              )}
              Manage billing
            </Button>
          )}
        </Card>
      )}

      {/* Billing toggle */}
      <div className="flex items-center gap-3">
        <div className="flex items-center bg-muted rounded-lg p-1 gap-1">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              billing === 'monthly' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('annual')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              billing === 'annual' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Annual
          </button>
        </div>
        {billing === 'annual' && (
          <Badge className="bg-green-100 text-green-800 border-green-200 text-xs font-medium">
            Save up to 28%
          </Badge>
        )}
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map((plan) => {
          const tierId = getTierId(plan);
          const current = isCurrentPlan(plan);
          const price = billing === 'annual' ? plan.annualPrice : plan.monthlyPrice;
          const interval = billing === 'annual' ? '/yr' : '/mo';
          return (
            <Card
              key={plan.id}
              className={`p-6 flex flex-col gap-5 transition-shadow hover:shadow-md ${
                plan.highlight ? 'border-[#1A2E4A]/40' : ''
              } ${current ? 'ring-2 ring-[#1A2E4A]' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-lg">{plan.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{plan.tagline}</p>
                </div>
                {current && (
                  <Badge className="bg-[#1A2E4A] text-white text-xs shrink-0">Current</Badge>
                )}
              </div>

              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{price}</span>
                  <span className="text-sm text-muted-foreground">{interval}</span>
                </div>
                {billing === 'annual' && (
                  <p className="text-xs text-green-700 mt-0.5">
                    {plan.annualBilled} · {plan.annualSavings}
                  </p>
                )}
              </div>

              <ul className="space-y-2 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#1A2E4A]" />
                    {f}
                  </li>
                ))}
                {/* Data commitment — on every paid plan */}
                <li className="flex items-start gap-2 text-sm pt-1 border-t border-dashed border-border mt-1">
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-green-700" />
                  <span className="text-green-800">Your data is never sold or shared</span>
                </li>
              </ul>

              <Button
                className={
                  plan.highlight
                    ? 'w-full bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white'
                    : 'w-full'
                }
                variant={plan.highlight ? 'default' : 'outline'}
                onClick={() => startCheckout(tierId)}
                disabled={loading !== null || current}
              >
                {loading === tierId ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : current ? (
                  'Current plan'
                ) : (
                  `Get ${plan.name}`
                )}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* $1 Trial CTA */}
      {!isSubscribed && !subscriptionLoading && (
        <div className="rounded-xl border border-border bg-muted/30 px-6 py-5 text-center">
          <p className="text-sm font-medium">Not ready to commit?</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">
            Try full Personal access for <strong>$1 for 30 days</strong> — all platforms, 20 rules, AI scoring.
            Continues at $9/month. Cancel any time, no questions asked.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => startCheckout('trial')}
            disabled={loading !== null}
          >
            {loading === 'trial' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : null}
            Start $1 Trial — 30 Days Full Access
          </Button>
        </div>
      )}


      {/* Coming soon for Personal subscribers */}
      <div className="rounded-xl border border-border bg-muted/30 p-5 sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full px-2.5 py-0.5">
            Personal plan feature
          </span>
        </div>
        <h2 className="text-base font-semibold tracking-tight mb-4">
          Coming soon for Personal
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              icon: <Mail className="h-4 w-4 text-blue-500" />,
              title: 'Weekly AI Briefing',
              description: "Claude reads your week's feed and writes you a 5-minute summary. Know what mattered without opening the app.",
            },
            {
              icon: <MessageCircle className="h-4 w-4 text-violet-500" />,
              title: 'Chat with Your Feed',
              description: 'Ask Claude anything about your curated content. "What did I miss about AI this week?" Powered by your actual feed.',
            },
            {
              icon: <Sparkles className="h-4 w-4 text-amber-500" />,
              title: 'Email Digest',
              description: "Your curated feed delivered to your inbox, daily or weekly. Finally, a newsletter that's actually yours.",
            },
            {
              icon: <Wand2 className="h-4 w-4 text-emerald-500" />,
              title: 'Smart Rule Suggestions',
              description: 'Claude notices your patterns and suggests rules you might be missing. Your feed gets smarter automatically.',
            },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-border bg-background p-3 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-1.5">
                <div className="p-1 rounded-md bg-muted/60">{item.icon}</div>
                <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 shrink-0 leading-none flex items-center">
                  Coming soon
                </span>
              </div>
              <p className="text-xs font-semibold leading-snug">{item.title}</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Founding Member */}
      <Card className="p-4 border-amber-200 bg-amber-50">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Star className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">
                {FOUNDING.name} — {FOUNDING.price}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">{FOUNDING.tagline}</p>
            </div>
          </div>
          {isFounding ? (
            <Badge className="bg-amber-500 text-white shrink-0">Active ✦</Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => startCheckout(FOUNDING.id)}
              disabled={loading !== null || isFounding}
            >
              {loading === FOUNDING.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Join — 500 spots only'
              )}
            </Button>
          )}
        </div>
      </Card>

      <p className="text-center text-xs text-muted-foreground">
        Cancel anytime · Secure checkout via Stripe · No hidden fees
        {isSubscribed && (
          <>
            {' · '}
            <button
              onClick={openBillingPortal}
              className="underline hover:text-foreground"
              disabled={portalLoading}
            >
              Manage payment method
            </button>
          </>
        )}
      </p>
    </div>
  );
}
